import clsx from "clsx";
import type dayjs from "dayjs";
import { useEffect, useState } from "react";
import useMedia from "use-media";
import type { DayOfWeek } from "../Calendar";
import { composeEventHandlers } from "../utils";
import { DateRangePickerCalendar } from "./DateRangePickerCalendar";
import { DateRangePickerDialog } from "./DateRangePickerDialog";
import { DateRangePickerInputLike } from "./DateRangePickerInputLike";
import { DateRangePickerPopover } from "./DateRangePickerPopover";

type DayChange = (day: dayjs.Dayjs) => void;

interface DateRangePickerProps {
  firstDayOfWeek?: DayOfWeek;

  startDayInputID?: string;
  startDay?: dayjs.Dayjs;
  defaultStartDay?: dayjs.Dayjs;
  onStartDayChange?: DayChange;

  endtDayInputID?: string;
  endDay?: dayjs.Dayjs;
  defaultEndDay?: dayjs.Dayjs;
  onEndDayChange?: DayChange;
}

export function DateRangePicker({
  firstDayOfWeek,
  startDay,
  defaultStartDay,
  onStartDayChange,
  endDay,
  defaultEndDay,
  onEndDayChange,
}: DateRangePickerProps) {
  const [startDayFocused, setStartDayFocused] = useState(false);
  const [endDayFocused, setEndDayFocused] = useState(false);

  const handleStartDayChange = composeEventHandlers(onStartDayChange, () => {
    setStartDayFocused(false);
  });
  const handleEndDayChange = composeEventHandlers(onEndDayChange, () => {
    setEndDayFocused(false);
  });

  useEffect(() => {
    // 開始日が変更された場合に日付を調整
    if (!startDay || !endDay) return;

    const today = startDay?.format("YYYY-MM-DD") === endDay?.format("YYYY-MM-DD");
    const disabled = today ? false : startDay?.isAfter(endDay) || false;
    if (disabled) {
      handleEndDayChange(startDay);
    }
  }, [startDay, endDay, handleEndDayChange]);

  const isSmall = useMedia({ maxWidth: "639px" });

  return (
    <div className={clsx("border", "border-gray-400", "rounded")}>
      <div className={clsx("flex", "items-center", "justify-between")}>
        <div className={clsx("min-w-0", "w-[calc(50%-1rem)]")}>
          {isSmall ? (
            <DateRangePickerDialog
              open={startDayFocused}
              onOpenChange={setStartDayFocused}
              triggerNode={
                <DateRangePickerInputLike placeholder="開始日を選択" focused={startDayFocused} day={startDay} />
              }
              contentNode={
                <DateRangePickerCalendar
                  firstDayOfWeek={firstDayOfWeek}
                  day={startDay}
                  defaultDay={defaultStartDay}
                  onDayChange={handleStartDayChange}
                />
              }
            />
          ) : (
            <DateRangePickerPopover
              open={startDayFocused}
              onOpenChange={setStartDayFocused}
              triggerNode={
                <DateRangePickerInputLike placeholder="開始日を選択" focused={startDayFocused} day={startDay} />
              }
              contentNode={
                <DateRangePickerCalendar
                  firstDayOfWeek={firstDayOfWeek}
                  day={startDay}
                  defaultDay={defaultStartDay}
                  onDayChange={handleStartDayChange}
                />
              }
            />
          )}
        </div>

        <div className="min-w-0">
          <div
            className={clsx(
              "min-w-[2rem]",
              "flex",
              "items-center",
              "justify-center",
              "text-sm",
              "leading-normal",
              "font-black"
            )}
          >
            <span>→</span>
          </div>
        </div>

        <div className={clsx("min-w-0", "w-[calc(50%-1rem)]")}>
          {isSmall ? (
            <DateRangePickerDialog
              open={endDayFocused}
              onOpenChange={setEndDayFocused}
              triggerNode={<DateRangePickerInputLike placeholder="終了日を選択" focused={endDayFocused} day={endDay} />}
              contentNode={
                <DateRangePickerCalendar
                  firstDayOfWeek={firstDayOfWeek}
                  minDay={startDay}
                  day={endDay}
                  defaultDay={defaultEndDay}
                  onDayChange={handleEndDayChange}
                />
              }
            />
          ) : (
            <DateRangePickerPopover
              open={endDayFocused}
              onOpenChange={setEndDayFocused}
              triggerNode={<DateRangePickerInputLike placeholder="終了日を選択" focused={endDayFocused} day={endDay} />}
              contentNode={
                <DateRangePickerCalendar
                  firstDayOfWeek={firstDayOfWeek}
                  minDay={startDay}
                  day={endDay}
                  defaultDay={defaultEndDay}
                  onDayChange={handleEndDayChange}
                />
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
